import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUpRight,
  CircleDot,
  CupSoda,
  Layers,
  Package,
  ScrollText,
  TrendingUp,
  UtensilsCrossed,
} from 'lucide-react';
import styles from './ProductsSection.module.css';

const SLIDE_MS = 4800;
const easeOut = [0.22, 1, 0.36, 1];

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: easeOut },
  },
};

const panelVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut, delay: 0.08 },
  },
};

const spotlightVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 82, damping: 16, delay: 0.12 },
  },
};

const sideCardVariants = {
  hidden: { opacity: 0, x: 18 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 88,
      damping: 18,
      delay: 0.2 + i * 0.08,
    },
  }),
};

const slideMotion = {
  initial: { opacity: 0, x: 28 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -28 },
  transition: { duration: 0.45, ease: easeOut },
};

const bodyMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.35, ease: easeOut },
};

const sliderProducts = [
  {
    id: 'food-wrap',
    name: 'Food Wrap Paper',
    slug: '/products/food-wrap-paper',
    category: 'Food Packaging',
    icon: Package,
    image: '/images/products/Food-Wrap-Paper.jpeg',
    tagline:
      'Grease-resistant food wrap paper for fresh presentation, sandwich packs, and bakery display — trusted by QSR, bakery, and takeaway brands across India.',
    chip: 'High demand',
    tags: ['Grease Resistant', 'Food Safe', 'Multi Use'],
  },
  {
    id: 'cup-sleeves',
    name: 'Paper Cup Sleeves',
    slug: '/products/paper-cup-sleeves',
    category: 'Hot Beverage',
    icon: CupSoda,
    image: '/images/products/Paper-Cup-Sleeves.jpeg',
    tagline:
      'Heat-protection paper cup sleeves with a smooth print surface for cafés, QSR chains, and branded hot-beverage cup programs that need consistent colour and fit.',
    chip: 'Custom print',
    tags: ['135–350 GSM', 'Food Grade', 'Custom Sizes'],
  },
  {
    id: 'bobbins',
    name: 'Bobbins',
    slug: '/products/pe-coated-rolls',
    category: 'Cup Raw Material',
    icon: CircleDot,
    image: '/images/products/bobbins.jpeg',
    tagline:
      'Cup-stock paper bobbins engineered for high-speed converting lines, with dependable strength, sealing performance, and consistent reel quality for manufacturers.',
    chip: 'Industrial',
    tags: ['140–300 GSM', 'High Strength', 'Custom Sizes'],
  },
  {
    id: 'ripple',
    name: 'Ripple Paper Cup Sleeves',
    slug: '/products/ripple-paper-cup-sleeves',
    category: 'Insulated Sleeve',
    icon: Layers,
    image: '/images/products/Ripple-Paper-Cup-Sleeves.jpeg',
    tagline:
      'Triple-layer ripple paper cup sleeves that improve insulation, grip, and hand safety for hot tea, coffee, and takeaway beverage service.',
    chip: 'Premium',
    tags: ['Triple Layer', 'Heat Resistant', 'Premium Finish'],
  },
  {
    id: 'pe-coated',
    name: 'PE Coated Rolls',
    slug: '/products/pe-coated-rolls',
    category: 'Moisture Resistant',
    icon: ScrollText,
    image: '/images/products/pe-coated-rolls.png',
    tagline:
      'PE coated paper rolls for hygienic food wrapping, pouches, sachets, and industrial packaging applications with reliable moisture-barrier performance.',
    chip: 'B2B',
    tags: ['PE Coated', 'Food Grade', 'High Durability'],
  },
  {
    id: 'plates',
    name: 'Paper Plates',
    slug: '/products/paper-plates',
    category: 'Tableware',
    icon: UtensilsCrossed,
    image: '/images/products/Paper-Plates.jpeg',
    tagline:
      'Sturdy disposable paper plates for catering, events, and food service — clean edges, stable rigidity, and food-contact safe materials for everyday use.',
    chip: 'Catering',
    tags: ['80–300 GSM', 'Leak Resistant', 'Multiple Types'],
  },
];

const stackProducts = [
  {
    id: 1,
    name: 'Paper Cup Sleeves',
    slug: '/products/paper-cup-sleeves',
    category: 'Hot Beverage',
    icon: CupSoda,
    image: '/images/products/Paper-Cup-Sleeves.jpeg',
    tagline:
      'Heat-protection sleeves with smooth print for cafés, QSR chains, and branded cup programs that need consistent colour, fit, and runnability.',
    chip: 'Custom print',
  },
  {
    id: 2,
    name: 'Bobbins',
    slug: '/products/pe-coated-rolls',
    category: 'Cup Raw Material',
    icon: CircleDot,
    image: '/images/products/bobbins.jpeg',
    tagline:
      'Cup-stock bobbins for high-speed converting lines — dependable strength, sealing, and reel quality for cup and sleeve manufacturers.',
    chip: 'Industrial',
  },
  {
    id: 3,
    name: 'Ripple Paper Cup Sleeves',
    slug: '/products/ripple-paper-cup-sleeves',
    category: 'Insulated Sleeve',
    icon: Layers,
    image: '/images/products/Ripple-Paper-Cup-Sleeves.jpeg',
    tagline:
      'Triple-layer ripple sleeves for better grip, insulation, and safety with hot beverages in cafés, QSR outlets, and takeaway service.',
    chip: 'Premium',
  },
];

const moreProducts = [
  {
    id: 4,
    name: 'PE Coated Rolls',
    slug: '/products/pe-coated-rolls',
    category: 'Moisture Resistant',
    icon: ScrollText,
    image: '/images/products/pe-coated-rolls.png',
    tagline:
      'PE coated rolls for hygienic food wrapping, pouches, and industrial packaging applications with food-grade barrier properties.',
    chip: 'B2B',
  },
  {
    id: 5,
    name: 'Paper Plates',
    slug: '/products/paper-plates',
    category: 'Tableware',
    icon: UtensilsCrossed,
    image: '/images/products/Paper-Plates.jpeg',
    tagline:
      'Sturdy disposable plates for catering and food service — clean edges, rigidity, and reliable performance for hot and oily foods.',
    chip: 'Catering',
  },
];

const ProductsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index) => {
    setActiveIndex((index + sliderProducts.length) % sliderProducts.length);
  }, []);

  const next = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  useEffect(() => {
    if (paused) return undefined;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const timer = window.setInterval(next, SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [paused, next]);

  const active = sliderProducts[activeIndex];
  const ActiveIcon = active.icon;

  return (
    <section
      className={`${styles.section} home-bg-linen`}
      aria-labelledby="premium-products-heading"
    >
      <div className={styles.bgAccent} aria-hidden="true">
        <span className={styles.orbOrange} />
        <span className={styles.orbGreen} />
        <span className={styles.meshGrid} />
      </div>

      <div className={styles.container}>
        <motion.header
          className={styles.header}
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
        >
          <h2 id="premium-products-heading" className={styles.title}>
            Our <span className={styles.highlight}>Premium Products</span>
          </h2>
          <p className={styles.subtitle}>
            Food-grade paper solutions manufactured in Meerut — reliable quality for brands across
            India and export markets.
          </p>
        </motion.header>

        <motion.div
          className={styles.panel}
          variants={panelVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.12 }}
        >
          <motion.article
            className={`${styles.card} ${styles.spotlightCard}`}
            variants={spotlightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
          >
            <div className={styles.spotlightGlow} aria-hidden="true" />

            <div className={styles.spotlightRibbon}>
              <TrendingUp size={13} aria-hidden="true" />
              Featured product
            </div>

            <div
              className={styles.spotlightMedia}
              aria-live="polite"
              aria-atomic="true"
              aria-label={`Product showcase: ${active.name}`}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={active.id}
                  className={styles.slideFrame}
                  {...slideMotion}
                >
                  <Link
                    to={active.slug}
                    className={styles.slideLink}
                    aria-label={`View ${active.name}`}
                  >
                    <img
                      src={active.image}
                      alt={active.name}
                      className={styles.spotlightImg}
                      loading={activeIndex === 0 ? 'eager' : 'lazy'}
                    />
                  </Link>
                  <span className={styles.statusBadge}>
                    <ActiveIcon size={12} aria-hidden="true" />
                    {active.category}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className={styles.slideDots} role="tablist" aria-label="Choose product">
              {sliderProducts.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={item.name}
                  className={`${styles.slideDot} ${index === activeIndex ? styles.slideDotActive : ''}`}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={active.id}
                className={styles.spotlightBody}
                {...bodyMotion}
              >
                <span className={styles.chip}>{active.chip}</span>
                <h3 className={styles.spotlightTitle}>{active.name}</h3>
                <p className={styles.spotlightText}>{active.tagline}</p>
                <div className={styles.tagRow}>
                  {active.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
                <Link to={active.slug} className={styles.spotlightBtn}>
                  View specs
                  <ArrowUpRight size={15} aria-hidden="true" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </motion.article>

          <div className={styles.sideStack}>
            <p className={styles.sideStackLabel}>Also in our range</p>
            {stackProducts.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.id}
                  className={`${styles.card} ${styles.sideCard}`}
                  custom={index}
                  variants={sideCardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <span className={styles.sideIndex} aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  <Link
                    to={item.slug}
                    className={styles.sideThumb}
                    aria-label={`View ${item.name}`}
                  >
                    <motion.img
                      src={item.image}
                      alt=""
                      className={styles.sideThumbImg}
                      loading="lazy"
                      whileHover={{ scale: 1.06 }}
                      transition={{ duration: 0.32 }}
                    />
                  </Link>

                  <div className={styles.sideContent}>
                    <span className={styles.sideStatus}>
                      <Icon size={12} aria-hidden="true" />
                      {item.category}
                    </span>
                    <h3 className={styles.sideTitle}>{item.name}</h3>
                    <p className={styles.sideTagline}>{item.tagline}</p>
                    <div className={styles.sideFooter}>
                      <span className={styles.chipMuted}>{item.chip}</span>
                      <Link
                        to={item.slug}
                        className={styles.sideArrowBtn}
                        aria-label={`View ${item.name}`}
                      >
                        <ArrowUpRight size={15} aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className={styles.moreRow}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45, ease: easeOut, delay: 0.1 }}
        >
          {moreProducts.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.id}
                className={`${styles.card} ${styles.sideCard}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.08 + index * 0.06, duration: 0.4, ease: easeOut }}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
              >
                <Link to={item.slug} className={styles.sideThumb} aria-label={`View ${item.name}`}>
                  <img src={item.image} alt="" className={styles.sideThumbImg} loading="lazy" />
                </Link>
                <div className={styles.sideContent}>
                  <span className={styles.sideStatus}>
                    <Icon size={12} aria-hidden="true" />
                    {item.category}
                  </span>
                  <h3 className={styles.sideTitle}>{item.name}</h3>
                  <p className={styles.sideTagline}>{item.tagline}</p>
                  <div className={styles.sideFooter}>
                    <span className={styles.chipMuted}>{item.chip}</span>
                    <Link
                      to={item.slug}
                      className={styles.sideArrowBtn}
                      aria-label={`View ${item.name}`}
                    >
                      <ArrowUpRight size={15} aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </motion.div>

        <motion.div
          className={styles.footer}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          <Link to="/products" className={styles.footerLink}>
            View all products
            <ArrowUpRight size={15} strokeWidth={2.5} aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;
